"use client";
import { PermissionTable } from "@/features/permissions/components/PermissionsTable";
import { SharedPermissionTable } from "@/features/permissions/components/SharedPermissionsTable";
import { ToggleIsApprovablePermission } from "@/features/permissions/components/ToggleIsApprovablePermission";
import { AddAssignmentButton } from "@/features/reserve/components/resources/tabs/access-restrictions/AddAssignmentButton";
import { ExternalLink } from "@cmp-src/primitives/external-link";
import { usePermissions } from "@/features/permissions/hooks/usePermissions";
import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from "@/features/reserve/hooks/useResourceSettings";
import { useResourcesPermissions } from "@/features/permissions/hooks/useResourcesPermissions";
import { useToggle } from "@/lib/hooks/useToggle";
import { api } from "@/lib/api/api";
import { areAll } from "@/lib/utils";
import { IResource, IResourcePermission } from "@gettactic/api";
import { HookFormTimeSelectDate } from "@cmp-src/form/TimeSelect";
import { HookFormCheckboxInput } from "@gettactic/components/src/form/CheckboxInput";
import { RadioInput } from "@gettactic/components/src/form/RadioInput";
import { HookFormToggleInput } from "@gettactic/components/src/form/ToggleInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { Flex } from "@gettactic/components/src/primitives/Flex";
import { Icon } from "@gettactic/components/src/primitives/Icon";
import { Stack } from "@gettactic/components/src/primitives/Stack";
import { useDisclosure, useStandardApiHandler } from "@gettactic/helpers";
import { getTimeDateObjectFromString } from "@gettactic/helpers/src/date-time/getTimeDateObjectFromString";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { IoArrowDown, IoArrowForward } from "react-icons/io5";
import { InformationPopover } from "@/ui/_to_replace/InformationPopover/InformationPopover";

interface AccessRestrictionsFormData {
  accessType: "open-access" | "restricted-access";
  hasAdditionalAccess: boolean;
  startsOn: string;
  startsAt: Date;
  allUsers: boolean;
}

type Props = {
  resources: IResource[];
  mutateAll: (err: boolean) => void;
};

export function groupSharedKey(perm: IResourcePermission) {
  return `${perm.user?.id}_${perm.team?.id}_${perm.since}_${perm.until}_${perm.is_approvable ? "true" : "false"}_${perm.access_group_type}`;
}

export type IResourcePermissionWithResName = IResourcePermission & {
  resName: string;
};

export type ConfirmOperationModal = {
  operation: "delete" | "is_approvable";
  applyTo: IResourcePermission[];
};

interface ChangeAccessDisclosureData {
  type: "open-access" | "restricted-access";
}

const getEmptyTimeDate = () => {
  const date = new Date();
  //date.setHours(0, 0, 0, 0);
  return date;
};

export function RestrictionsTab({ resources, mutateAll }: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [otherRestrictions, toggleOtherRestrictions] = useToggle();
  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const { resourceIds, additionalAccess, refresh } = usePermissions(
    resources.map((resource) => resource.id)
  );

  const queryClient = useQueryClient();
  const permissions = useResourcesPermissions(resources);
  const accessChangeDisclosure = useDisclosure<ChangeAccessDisclosureData>();
  const resourceContext = useResourceSettingsState();

  const { wrapApiRequest, isLoading } = useStandardApiHandler();

  const allRestricted = areAll(
    resources.map((x) => x.is_open),
    false
  );

  const [accessMode, setAccessMode] = React.useState<
    "open-access" | "restricted-access"
  >(!allRestricted ? "open-access" : "restricted-access");

  const form = useForm<AccessRestrictionsFormData>({
    defaultValues: {
      hasAdditionalAccess: false,
      startsOn: "0",
      startsAt: getEmptyTimeDate(),
      allUsers: false
    }
  });

  const _hasAdditionalAccess = form.watch("hasAdditionalAccess");
  const hasAdditionalAccess =
    accessMode === "restricted-access" && _hasAdditionalAccess;

  const isAllUsersEnabled = form.watch("allUsers");

  const onAddAssignmentClose = () => {
    refresh();
    mutateAll(false);
  };

  const handleAccessChange = wrapApiRequest(
    async () => {
      const mode = accessChangeDisclosure.data?.type;
      if (!mode) return;

      setAccessMode(mode);

      if (!resources.length) {
        throw new Error("Resource not found");
      }

      const newResources = resources.map((resource) => ({
        ...resource,
        is_open: mode === "open-access"
      }));
      const result = newResources.map(async (newResource) => {
        await api.client.organizations.resourcePut(newResource.id, newResource);
      });
      await Promise.all(result);

      // should be only one resource at a time always
      newResources.forEach((newResource) => {
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: newResource
        });
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["organizations.resourcesAll"]
        }),
        queryClient.invalidateQueries({
          queryKey: ["organizations.resourceScheduledPermissionsAll"]
        }),
        queryClient.invalidateQueries({
          queryKey: ["organizations.getResourceAdditionalAccess"]
        })
      ]);
      await mutateAll(true);
      accessChangeDisclosure.close();
    },
    resources.length === 1
      ? "Resource access updated"
      : "Resources access updated"
  );

  const handleUpdateOptions = form.handleSubmit(
    wrapApiRequest(async (data) => {
      if (!resources.length) {
        throw new Error("Resource not found");
      }
      const result = resources.map(async (resource) => {
        await api.client.organizations.setResourceAdditionalAccessOptions(
          resource.id,
          {
            enabled: data.hasAdditionalAccess,
            starts_on: Number(data.startsOn),
            starts_at: `${data.startsAt.getHours()}:${data.startsAt.getMinutes()}`,
            all_users: data.allUsers
          }
        );
      });
      await Promise.all(result);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["organizations.resourcesAll"]
        }),
        queryClient.invalidateQueries({
          queryKey: ["organizations.resourceScheduledPermissionsAll"]
        }),
        queryClient.invalidateQueries({
          queryKey: ["organizations.getResourceAdditionalAccess"]
        })
      ]);
    }, "Resource access updated")
  );

  const sanitizedAdditionalAccess = JSON.stringify(additionalAccess);
  const sanitizedResource = JSON.stringify(resources);

  React.useEffect(() => {
    if (!additionalAccess || !additionalAccess[0].length) return;
    const resource = resources[0];
    if (!resource) return;
    if (sanitizedAdditionalAccess) {
      const nDate = getTimeDateObjectFromString(
        additionalAccess?.[0]?.[0]?.starts_at || ""
      );

      const allHasAllUsers = areAll(
        additionalAccess.map((x) => x[0]?.all_users),
        true
      );

      form.reset({
        hasAdditionalAccess: additionalAccess[0][0].enabled,
        startsOn: String(additionalAccess[0][0].starts_on || 0),
        startsAt: nDate,
        allUsers: allHasAllUsers
      });
    }
  }, [sanitizedAdditionalAccess, sanitizedResource]);

  const startsOnItems = [
    { id: "0", name: "Same Day" },
    { id: "-1", name: "1 Day Before" },
    { id: "-2", name: "2 Days Before" },
    { id: "-3", name: "3 Days Before" },
    { id: "-4", name: "4 Days Before" },
    { id: "-5", name: "5 Days Before" },
    { id: "-6", name: "6 Days Before" },
    { id: "-7", name: "1 Week Before" }
  ];

  return (
    <Stack>
      <Flex className={"w-full gap-6"}>
        <div className={"w-full"}>
          <RadioInput
            checked={accessMode === "open-access"}
            onChange={() => {
              accessChangeDisclosure.open({ type: "open-access" });
            }}
            radioPosition="left"
            label={
              <Flex className={"flex-col gap-0.5"}>
                <Icon name={"Users"} className={"text-disabled"} />
                <span className={"font-bold text-lg"}>Open Access</span>
              </Flex>
            }
            description={
              resources.length === 1
                ? "Anybody can reserve this resource."
                : "Anybody can reserve these resources."
            }
          />
        </div>
        <div className={"w-full"}>
          <RadioInput
            checked={accessMode === "restricted-access"}
            onChange={() => {
              accessChangeDisclosure.open({ type: "restricted-access" });
            }}
            radioPosition="left"
            label={
              <Flex className={"flex-col gap-0.5"}>
                <Icon name={"Lock"} className={"text-disabled"} />
                <span className={"font-bold text-lg"}>Restricted Access</span>
              </Flex>
            }
            description={
              resources.length === 1
                ? "Only those assigned to this resource can reserve it."
                : "Only those assigned to these resources can reserve it."
            }
          />
        </div>
      </Flex>

      <div>
        <HookFormToggleInput
          form={form}
          name="hasAdditionalAccess"
          label="Priority Booking"
          description="If no reservations have been made by the primary group, then you can optionally add access to a secondary group. Select when the secondary group gains access."
          onChange={() => handleUpdateOptions()}
          disabled={accessMode === "open-access"}
          togglePosition="left"
        />
        {hasAdditionalAccess ? (
          <Stack className="px-8 pt-2 pb-6 gap-4">
            <Flex className={"items-center gap-6"}>
              <HookFormUnifiedSelectInput
                form={form}
                name="startsOn"
                label="Days"
                items={startsOnItems}
                className="max-w-[200px]"
                onChange={() => handleUpdateOptions()}
              />
              <HookFormTimeSelectDate
                form={form}
                name="startsAt"
                label="Time"
                interval={15}
                placeholder="Select time"
                onChange={() => handleUpdateOptions()}
              />
            </Flex>
            <HookFormCheckboxInput
              form={form}
              name="allUsers"
              label={
                <>
                  Add
                  <span className="mx-1 font-bold capitalize">
                    All {authenticatedUser?.organization?.name} Users
                  </span>
                  to the Secondary Assignment Group
                </>
              }
              onChange={() => handleUpdateOptions()}
              checkboxPosition="left"
            />
          </Stack>
        ) : null}
      </div>
      <ConfirmModal
        isOpen={accessChangeDisclosure.isOpen}
        onOpenChange={accessChangeDisclosure.setOpen}
        onConfirm={handleAccessChange}
        title={"Change Resource's Access Type"}
        message={
          <>
            Are you sure you want to change the access type of the selected
            resources to{" "}
            <strong>{accessChangeDisclosure.data?.type || ""}</strong> ?
          </>
        }
      />
      <div className="mt-4 flex items-center justify-between pb-2">
        <div className="flex items-center justify-center">
          <h3 className="font-medium text-2xl text-neutral-900 leading-6">
            Assignments
          </h3>

          <InformationPopover>
            <span>
              Assignments can be used either to restrict a resource or to
              require approval for specific teams &amp; users. See{" "}
              <ExternalLink href="https://gettactic.zendesk.com/hc/en-us/articles/27811510535067-Add-Edit-or-Delete-Workspaces">
                the help article
              </ExternalLink>{" "}
              for additional details.
            </span>
          </InformationPopover>
        </div>

        <AddAssignmentButton
          resources={resources}
          hasAdditionalAccess={hasAdditionalAccess}
          isAllUsersEnabled={isAllUsersEnabled}
          onRefresh={onAddAssignmentClose}
        />
      </div>

      <ToggleIsApprovablePermission
        isOpen={
          operationModal?.operation === "is_approvable" &&
          operationModal.applyTo.length > 0
        }
        onOpenChange={(open) => {
          if (!open) {
            setOperationModal(null);
            mutateAll(false);
          }
        }}
        resourceIds={resourceIds}
        permissions={operationModal?.applyTo ?? []}
      />

      {resources.length > 1 && permissions.shared.length ? (
        <h3 className="mt-6 mb-4 font-bold text-lg text-secondary">
          Shared Assignments with the Selected Resources ({resources.length})
        </h3>
      ) : null}

      {permissions.shared?.length ? (
        <div className="mt-6">
          <SharedPermissionTable
            setOperationalModal={setOperationModal}
            data={permissions.shared}
            inGroups={permissions.inGroups}
            onDeleted={() => mutateAll(false)}
          />
        </div>
      ) : null}

      {resources.length > 1 && permissions.rest.length ? (
        <>
          <h3
            onClick={toggleOtherRestrictions}
            className="mt-6 mb-4 cursor-pointer font-bold text-lg text-secondary"
          >
            {otherRestrictions ? (
              <IoArrowDown className="inline h-4 w-4" />
            ) : (
              <IoArrowForward className="inline h-4 w-4" />
            )}{" "}
            Non-Shared Assignments ({permissions.rest.length})
          </h3>
          {otherRestrictions ? (
            <PermissionTable
              setOperationalModal={setOperationModal}
              data={permissions.rest}
            />
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
